import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle2, Loader2, Sparkles, Phone } from 'lucide-react';
import { contactInfo } from '../mock';
import { submitInquiry } from '../utils/leads';

// Global proposal enquiry modal.
// Open from anywhere via: window.dispatchEvent(new CustomEvent('open-proposal'))
const inp = "w-full px-4 py-3 rounded-xl border text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C9A96E] transition-all";
const inpStyle = { borderColor: 'rgba(201,169,110,0.35)' };

const ProposalModal = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', eventDate: '', guestCount: '', message: '' });

  useEffect(() => {
    const handler = () => { setOpen(true); setSubmitted(false); setError(''); };
    window.addEventListener('open-proposal', handler);
    return () => window.removeEventListener('open-proposal', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { setError('Please enter your name and phone number.'); return; }
    setError(''); setSubmitting(true);
    try {
      await submitInquiry({
        name: form.name,
        phone: form.phone,
        email: form.email,
        eventType: 'Wedding Proposal Request',
        eventDate: form.eventDate,
        guestCount: form.guestCount,
        location: 'Dharamshala',
        message: form.message || 'Proposal request via Get Proposal button'
      }, 'get-proposal-modal');
      setSubmitted(true);
      setForm({ name: '', phone: '', email: '', eventDate: '', guestCount: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or call us on +91 86793 33354.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="proposal-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" style={{ border: '1px solid rgba(201,169,110,0.25)' }}>
        <button onClick={close} data-testid="proposal-modal-close" aria-label="Close" className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/90 shadow text-gray-500 hover:text-gray-800">
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="p-10 text-center" data-testid="proposal-modal-success">
            <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: 'rgba(201,169,110,0.12)' }}>
              <CheckCircle2 className="h-8 w-8" style={{ color: '#C9A96E' }} />
            </div>
            <h3 className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#2b2622' }}>Thank You!</h3>
            <p className="text-gray-500 text-sm leading-relaxed">We've received your request. Our team will reach out shortly with your personalised Dharamshala wedding proposal.</p>
            <button onClick={close} className="mt-6 px-6 py-2.5 rounded-full text-white text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)' }}>Close</button>
          </div>
        ) : (
          <div className="p-7 sm:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-3" style={{ background: 'rgba(201,169,110,0.1)', border: '1px solid rgba(201,169,110,0.3)' }}>
                <Sparkles className="h-4 w-4" style={{ color: '#C9A96E' }} />
                <span className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: '#9a7b43' }}>Free Wedding Proposal</span>
              </div>
              <h3 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif", color: '#2b2622' }}>Plan Your Dharamshala Wedding</h3>
              <p className="text-gray-500 text-sm mt-2">Tell us your date, guest count &amp; vision — we'll prepare a personalised proposal.</p>
            </div>

            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>}

            <form onSubmit={submit} noValidate className="space-y-3" data-testid="proposal-modal-form">
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your Name *" className={inp} style={inpStyle} data-testid="proposal-modal-name" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required type="tel" inputMode="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone / WhatsApp *" className={inp} style={inpStyle} data-testid="proposal-modal-phone" />
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" className={inp} style={inpStyle} data-testid="proposal-modal-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="date" value={form.eventDate} onChange={e => set('eventDate', e.target.value)} className={inp} style={inpStyle} data-testid="proposal-modal-date" />
                <input value={form.guestCount} onChange={e => set('guestCount', e.target.value)} placeholder="Guest Count" className={inp} style={inpStyle} data-testid="proposal-modal-guests" />
              </div>
              <textarea rows={3} value={form.message} onChange={e => set('message', e.target.value)} placeholder="Tell us about your dream wedding..." className={`${inp} resize-none`} style={inpStyle} data-testid="proposal-modal-message" />
              <button type="submit" disabled={submitting} data-testid="proposal-modal-submit" className="w-full py-3.5 rounded-full text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:scale-[1.01]" style={{ background: 'linear-gradient(135deg, #C9A96E, #B8943F)', boxShadow: '0 8px 24px rgba(201,169,110,0.35)' }}>
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Sending...</> : <><Send className="h-5 w-5" />Get My Free Proposal</>}
              </button>
            </form>

            {contactInfo?.phones?.[0] && (
              <a href={`tel:${contactInfo.phones[0]}`} className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-[#C9A96E] transition-colors" data-testid="proposal-modal-call">
                <Phone className="h-4 w-4" /> or call us at {contactInfo.phones[0]}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalModal;
